import React from 'react';
import { API } from '../api';
import { Field, Section, Toggle } from './ui';
import ClubSite from './ClubSite';

/*
 * The club's own public page, and the little builder behind it.
 *
 * The page is a club's website when it has none of its own, so the thing
 * this screen has to protect against is not a club doing something clever
 * but a club doing nothing: an empty frame with a name on it is worse than
 * no page at all. Hence fixed sections rather than blocks to arrange, a
 * switch per section, and a publish switch that refuses an empty page.
 *
 * Nothing here is computed from competitions, results or members. Every
 * field is something the club writes about itself, which is what makes the
 * public route safe to serve without a login at all.
 *
 * See CLUB_SITE_PLAN.md.
 */

const SECTION_LABEL = {
  /* The `welcome` key is what the server has always called it. What it holds
     is the tagline and everything the club writes about itself, which is
     "About the club" to anybody reading this screen. */
  welcome: 'About the club',
  photos: 'Photos',
  contact: 'Contact',
  links: 'Links',
};

/* Where an action can sit. The club decides, because a club that wants its
   join button in the masthead and nothing else should be able to have that. */
const PLACE_LABEL = {
  bar: 'In the top bar',
  hero: 'On the big picture',
  body: 'Down the page',
  foot: 'At the foot',
};

/* Where the page lives. Only cosmetic here — the server is the authority on
   what a valid address is — but an admin choosing one wants to see it whole. */
const SITE_HOST = 'greensheetbowls.online';

function ClubWebsitePanel({ clubId, showToast, onOpenPlan }) {
  const [data, setData] = React.useState(null);
  const [site, setSite] = React.useState(null);
  const [slug, setSlug] = React.useState('');
  const [editingSlug, setEditingSlug] = React.useState(false);
  /* The club's own domain (www.theirclub.co.nz) and whether it is working yet.
     See server/utils/clubHosts.js for how a visit to it reaches this page. */
  const [customDomain, setCustomDomain] = React.useState('');
  const [domainStatus, setDomainStatus] = React.useState(null);
  const [checkingDomain, setCheckingDomain] = React.useState(false);
  const [error, setError] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);
  /* The preview renders the real page component against the saved data, so
     what it shows is what a visitor gets rather than a second drawing of it
     that has to be kept in step. It is bumped on every save to force a
     refetch — the page has no idea the settings beside it just changed. */
  const [previewing, setPreviewing] = React.useState(false);
  const [previewKey, setPreviewKey] = React.useState(0);

  React.useEffect(() => {
    if (!clubId) return undefined;
    let cancelled = false;
    setData(null);
    fetch(`${API}/api/site/settings/${clubId}`)
      .then(async r => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.error || 'Could not load the club website settings.');
        return d;
      })
      .then(d => {
        if (cancelled) return;
        setData(d);
        setSite(d.site);
        setSlug(d.slug);
        setCustomDomain(d.customDomain || '');
        setDomainStatus(null);
        setDirty(false);
        setEditingSlug(false);
      })
      .catch(e => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [clubId]);

  const set = (k, v) => { setSite(s => ({ ...s, [k]: v })); setDirty(true); };

  const checkDomain = async () => {
    setCheckingDomain(true);
    try {
      const r = await fetch(`${API}/api/site/domain-status/${clubId}`);
      const d = await r.json().catch(() => ({}));
      setDomainStatus(r.ok ? d.state : 'error');
    } catch (e) {
      setDomainStatus('error');
    } finally {
      setCheckingDomain(false);
    }
  };
  const setContact = (k, v) => {
    setSite(s => ({ ...s, contact: { ...s.contact, [k]: v } }));
    setDirty(true);
  };
  const setSection = (k, on) => {
    setSite(s => ({ ...s, sections: { ...s.sections, [k]: on } }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/site/settings/${clubId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        /* The address only goes up when it is this person's to change.
           Sending it unchanged is harmless — the server compares before it
           refuses — but not sending it at all says what is meant. */
        body: JSON.stringify({ ...(data.canEditAddress ? { site, slug } : { site }), customDomain }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setError(d.error || 'That did not work.'); return; }
      setSite(d.site);
      setSlug(d.slug);
      setCustomDomain(d.customDomain || '');
      setDomainStatus(null);
      setDirty(false);
      setEditingSlug(false);
      setPreviewKey(k => k + 1);
      showToast && showToast('Saved');
    } catch (e) {
      setError(e.message || 'That did not work.');
    } finally {
      setSaving(false);
    }
  };

  const togglePhoto = id => {
    setSite(s => ({
      ...s,
      photoIds: s.photoIds.includes(id)
        ? s.photoIds.filter(x => x !== id)
        : (s.photoIds.length >= (data.maxPhotos || 12) ? s.photoIds : [...s.photoIds, id]),
    }));
    setDirty(true);
  };

  const setHeading = (k, v) => {
    setSite(s => ({ ...s, headings: { ...s.headings, [k]: v } }));
    setDirty(true);
  };
  const setAction = (kind, patch) => {
    set('actions', site.actions.map(a => (a.kind === kind ? { ...a, ...patch } : a)));
  };

  /* The default is not stored, so a picture nobody has adjusted has no entry
     at all — hence reading through a default here rather than assuming one. */
  const fitOf = id => (site.photoFit || {})[String(id)] || { fit: 'cover', focus: 'centre' };
  const setFit = (id, patch) => {
    setSite(s => ({
      ...s,
      photoFit: { ...(s.photoFit || {}), [String(id)]: { ...fitOf(id), ...patch } },
    }));
    setDirty(true);
  };

  const setStory = (i, patch) => {
    set('stories', site.stories.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  };
  const moveStory = (i, by) => {
    const next = [...site.stories];
    const [moved] = next.splice(i, 1);
    next.splice(i + by, 0, moved);
    set('stories', next);
  };

  const addLink = () => {
    set('links', [...site.links, { kind: 'facebook', url: '', label: '' }]);
  };
  const setLink = (i, patch) => {
    set('links', site.links.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  };

  if (error && !data) return <div className="poster-error" role="alert">{error}</div>;
  if (!data || !site) {
    return <p style={{ margin: 0, color: 'var(--gs-text-muted)' }}>Loading…</p>;
  }

  /* Not on this plan. Said here rather than by refusing to load, so an admin
     can see what they would be getting instead of meeting an error — and so
     the answer is the plan page rather than a shrug. */
  if (!data.allowed) {
    return (
      <Section label="Club website">
        <p style={{ marginTop: 0 }}>
          A club website is not part of the <strong>{data.planLabel}</strong> plan.
        </p>
        <p style={{ color: 'var(--gs-text-muted)' }}>
          It gives the club its own public page — who you are, what you do, where the
          green is, and how to join — at an address you can put on a poster. Members
          sign in from the same page.
        </p>
        <button type="button" className="w3-button w3-round poster-btn-primary"
          onClick={() => onOpenPlan && onOpenPlan()}>
          See the plans
        </button>
      </Section>
    );
  }

  const url = `${SITE_HOST}/${slug}`;
  /* The same emptiness test the server refuses to publish on, said before
     the switch is flicked rather than after. */
  const tooEmpty = !site.tagline.trim() && !site.stories.length && !site.photoIds.length;

  return (
    <>
      <Section label="The page">
        <label className="poster-switch">
          <Toggle
            ariaLabel="Publish this club's page"
            checked={!!site.published}
            disabled={tooEmpty && !site.published}
            onChange={() => set('published', !site.published)} />
          <span>Published</span>
        </label>
        <p className="poster-note" style={{ marginTop: 'var(--gs-space-2)' }}>
          {tooEmpty && !site.published
            ? 'Write a welcome or add a photo first — an empty page is worse than none.'
            : site.published
              ? 'Anybody with the address can see this page.'
              : 'Only your club’s admins can see it. They see it as a preview.'}
        </p>

        {/* The address is not the club's to change: it is a public URL in a
            namespace every club shares, and one club taking "bowls" affects
            the others. Assigned by whoever can see the whole namespace. */}
        <Field label="Address" htmlFor="site-slug"
          help={!data.canEditAddress
            ? 'Set by the GreenSheet administrator. Ask them if it needs changing.'
            : site.published
              ? 'Changing this breaks any link or poster already using the old one.'
              : 'Set this before the club publishes. It is fixed once the page is live.'}>
          {editingSlug ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ color: 'var(--gs-text-muted)' }}>{SITE_HOST}/</span>
              <input id="site-slug" className="w3-input" style={{ flex: '1 1 220px', maxWidth: 320 }}
                value={slug} maxLength={60}
                onChange={e => { setSlug(e.target.value); setDirty(true); }} />
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <code style={{ fontSize: 'var(--gs-text-sm)' }}>{url}</code>
              <button type="button" className="w3-button w3-round poster-btn"
                onClick={() => { navigator.clipboard?.writeText(`https://${url}`); showToast && showToast('Address copied'); }}>
                Copy
              </button>
              {data.canEditAddress && (
                <button type="button" className="w3-button w3-round poster-btn"
                  onClick={() => setEditingSlug(true)}>
                  Edit
                </button>
              )}
              {/* Works published or not: the server hands this club's own
                  admins a preview of an unpublished page, which is the whole
                  reason a club can see its page before anyone else can. */}
              <a className="w3-button w3-round poster-btn" href={`/${slug}`} target="_blank" rel="noreferrer">
                {site.published ? 'View page' : 'Open preview'}
              </a>
            </div>
          )}
        </Field>
      </Section>

      {/* The club's own address. The page above is reachable at
          greensheetbowls.online/<club> regardless; this puts it at the
          club's domain as well, without the visitor ever seeing ours. */}
      <Section label="Your own web address">
        <p className="poster-note" style={{ marginTop: 0 }}>
          Already have a website address, like <strong>www.yourclub.co.nz</strong>? Your page can
          appear there instead, with your address staying in the browser. Signing in and the
          membership form still happen on GreenSheet.
        </p>
        <Field label="Your domain" htmlFor="site-domain"
          help="Include the www. Leave blank if you do not have one.">
          <input id="site-domain" className="w3-input" style={{ maxWidth: 360 }}
            placeholder="www.yourclub.co.nz" value={customDomain} maxLength={200}
            onChange={e => { setCustomDomain(e.target.value.trim()); setDirty(true); setDomainStatus(null); }} />
        </Field>
        {customDomain && (
          <details className="domain-steps">
            <summary>How to connect it</summary>
            {/* Before the steps: the one thing that cannot be undone by
                changing a record back is visitors finding the old site gone. */}
            <p className="domain-warning">
              <strong>Before you start:</strong> once this is done, {customDomain} shows your GreenSheet
              page instead of any website it shows now. If you have a site there you want to keep,
              decide that first. Your club’s email is not affected.
            </p>
            <ol>
              <li>Save this page with your domain filled in. GreenSheet is told, and connects it on our side.</li>
              <li>Log in to whoever you pay for your domain — such as 1st Domains, Freeparking or
                Crazy Domains — or ask the member who set it up. Open the domain and look at its
                <strong> nameservers</strong>. If they belong to that company, carry on there. If they
                point somewhere else (Cloudflare, Wix, or a web designer), the next step happens in
                that service instead.</li>
              <li>In the domain’s <strong>DNS settings</strong> (sometimes called DNS Manager), add this record:
                <div className="domain-record">
                  <span>Type <code>CNAME</code></span>
                  <span>Name <code>{customDomain.split('.')[0]}</code></span>
                  <span>Value <code>greensheetbowls.onrender.com</code></span>
                </div>
                If there is already a record named <code>{customDomain.split('.')[0]}</code> — usually
                pointing at an old website — remove it first or change it to the one above. A name can
                only point one way, so the new record will be refused while the old one is there.
              </li>
              <li>If you also want the address without “www” to work, they can set it to forward
                to <strong>{customDomain.startsWith('www.') ? customDomain : 'www.' + customDomain}</strong>.</li>
              <li><strong>Do not change the records called MX.</strong> Those carry your club’s email.</li>
              <li>Changes can take up to a day to reach everyone. Use <strong>Check</strong> below to see how it is going.</li>
            </ol>
          </details>
        )}
        {customDomain && !dirty && (
          <div className="domain-check">
            <button type="button" className="w3-button w3-round poster-btn" onClick={checkDomain} disabled={checkingDomain}>
              {checkingDomain ? 'Checking…' : 'Check'}
            </button>
            {domainStatus === 'connected' && (
              <span className="domain-ok">Working — <a href={'https://' + customDomain} target="_blank" rel="noreferrer">{customDomain}</a> shows your page.</span>
            )}
            {domainStatus === 'pending' && <span>Your domain points at GreenSheet. We are finishing the connection on our side — this usually takes under a day.</span>}
            {domainStatus === 'dns' && <span>Your domain does not point at GreenSheet yet. Ask whoever manages it to add the record above.</span>}
            {domainStatus === 'error' && <span>Could not check just now. Try again in a minute.</span>}
          </div>
        )}
      </Section>

      {/* Each section carries its own switch, so "which of these do we want"
          is answered where the fields for it are, not in a separate list of
          switches somewhere else that has to be kept in step. */}
      {/* One section, not two.
        *
          "Welcome" was left holding a single Tagline box once the About text
          became a piece, and its switch governed a separate section further
          down called "More about the club" — so the screen had a heading
          nobody could explain and a switch whose effect was somewhere else.
          Both are the same thing: what the club says about itself. The
          switch sits on it and everything it turns off is inside it. */}
      <Section label={SECTION_LABEL.welcome} action={
        <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--gs-space-3)' }}>
          <span style={{ fontSize: 'var(--gs-text-sm)', color: 'var(--gs-text-muted)' }}>
            {site.stories.length} of {data.maxStories || 8} pieces
          </span>
          <Toggle ariaLabel="Show what the club says about itself"
            checked={!!site.sections.welcome}
            onChange={() => setSection('welcome', !site.sections.welcome)} />
        </span>
      }>
        <div className="gs-field-grid">
          {/* No example text inside the boxes. A grey "Bowls in the west since
              1928" sitting in an empty Tagline reads as something the page
              already says, and an admin scrolling this form cannot tell what
              is theirs and what is ours without clicking into every field.
              The guidance goes underneath, where it is plainly guidance. */}
          <Field label="Tagline" htmlFor="site-tagline"
            help="One line, over the big picture at the top.">
            <input id="site-tagline" className="w3-input" maxLength={120}
              value={site.tagline} onChange={e => set('tagline', e.target.value)} />
          </Field>
        </div>

        {/* One shape, repeated — a heading, some words, optionally a picture
            and a button — so there is nothing to arrange but the order and no
            way to make a page that looks broken. */}
        <p style={{ color: 'var(--gs-text-muted)', fontSize: 'var(--gs-text-sm)' }}>
          Pieces run down the page in this order. The first one is where most clubs
          say who they are.
        </p>
        {site.stories.map((s, i) => (
          <div key={i} className="ml-add-form" style={{ marginBottom: 'var(--gs-space-3)' }}>
            {/* Everything about the piece except its words on one row: the
                heading, the picture, whether it has a button, and the three
                controls that move or remove it. A piece with no button — most
                of them — is now three rows rather than four. */}
            <div className="gs-field-grid" style={{
              /* Narrower heading once a button appears, because the row gains
                 a column and something has to give; the heading is a few
                 words and the one that can spare it. */
              '--gs-cols': s.button
                ? '1.6fr 1fr 1fr 1.2fr auto auto auto'
                : '2fr 1fr 1fr auto auto auto',
            }}>
              <Field label="Heading">
                <input className="w3-input" maxLength={80} value={s.heading}
                  aria-label={`Piece ${i + 1} heading`}
                  onChange={e => setStory(i, { heading: e.target.value })} />
              </Field>
              <Field label="Picture">
                <select className="w3-select" value={s.photoId || ''}
                  aria-label={`Piece ${i + 1} picture`}
                  onChange={e => setStory(i, { photoId: e.target.value || null })}>
                  <option value="">No picture</option>
                  {data.photos.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
              </Field>
              <Field label="Button">
                <select className="w3-select" aria-label={`Piece ${i + 1} button`}
                  value={(s.button && s.button.kind) || ''}
                  onChange={e => setStory(i, {
                    button: e.target.value
                      ? { kind: e.target.value, label: '', url: '' }
                      : null,
                  })}>
                  <option value="">No button</option>
                  {(data.pieceButtonKinds || []).map(k => (
                    <option key={k.kind} value={k.kind}>{k.label}</option>
                  ))}
                </select>
              </Field>
              {s.button && (
                <Field label="Call it">
                  <input className="w3-input" maxLength={40}
                    aria-label={`Piece ${i + 1} button words`}
                    placeholder={(data.pieceButtonKinds || [])
                      .find(k => k.kind === s.button.kind && k.kind !== 'url')?.label || ''}
                    value={s.button.label || ''}
                    onChange={e => setStory(i, { button: { ...s.button, label: e.target.value } })} />
                </Field>
              )}
              <Field label=" ">
                <button type="button" className="ml-topic-bin" aria-label={`Move piece ${i + 1} up`}
                  disabled={i === 0} onClick={() => moveStory(i, -1)}>
                  <i className="fa fa-arrow-up" aria-hidden="true"></i>
                </button>
              </Field>
              <Field label=" ">
                <button type="button" className="ml-topic-bin" aria-label={`Move piece ${i + 1} down`}
                  disabled={i === site.stories.length - 1} onClick={() => moveStory(i, 1)}>
                  <i className="fa fa-arrow-down" aria-hidden="true"></i>
                </button>
              </Field>
              <Field label=" ">
                <button type="button" className="ml-topic-bin" aria-label={`Remove piece ${i + 1}`}
                  onClick={() => set('stories', site.stories.filter((_, j) => j !== i))}>
                  <i className="fa fa-trash-o" aria-hidden="true"></i>
                </button>
              </Field>
            </div>
            <Field label="What it says">
              <textarea className="w3-input" maxLength={3000}
                aria-label={`Piece ${i + 1} text`}
                style={{ minHeight: 110, resize: 'vertical' }}
                value={s.body} onChange={e => setStory(i, { body: e.target.value })} />
            </Field>
            {/* The only thing left below: an address, and only for a link of
                the club's own. It gets a row to itself because a URL is long
                and unreadable squeezed into a quarter of one. Everything
                else about the piece is on the top row now, so a piece with
                no button is two rows and one with a button still two. */}
            {s.button && s.button.kind === 'url' && (
              <Field label="Where it goes">
                <input className="w3-input" type="url" maxLength={300}
                  aria-label={`Piece ${i + 1} button address`}
                  value={s.button.url || ''}
                  onChange={e => setStory(i, { button: { ...s.button, url: e.target.value } })} />
              </Field>
            )}
          </div>
        ))}
        <button type="button" className="w3-button w3-round poster-btn"
          disabled={site.stories.length >= (data.maxStories || 8)}
          onClick={() => set('stories', [...site.stories, { heading: '', body: '', photoId: null, button: null }])}>
          <i className="fa fa-plus w3-margin-right"></i>Add a piece
        </button>
      </Section>

      <Section label={SECTION_LABEL.photos} action={
        <Toggle ariaLabel="Show photos" checked={!!site.sections.photos}
          onChange={() => setSection('photos', !site.sections.photos)} />
      }>
        {data.photos.length === 0 ? (
          <p style={{ margin: 0, color: 'var(--gs-text-muted)' }}>
            No photos in the club gallery yet. Add some under Gallery and they will appear here.
          </p>
        ) : (
          <>
            {/* Kept: the top photo does two things nothing on screen says —
                it heads the page and it is what shows when a link is shared. */}
            <p style={{ marginTop: 0, color: 'var(--gs-text-muted)', fontSize: 'var(--gs-text-sm)' }}>
              Up to {data.maxPhotos}. The top photo heads the page and is what shows
              when somebody shares the link.
            </p>
            <div className="photo-grid photo-grid-pick">
              {data.photos.map(p => {
                const picked = site.photoIds.includes(p.id);
                const isHero = String(site.heroPhotoId) === String(p.id);
                return (
                  <div key={p.id}>
                    <button type="button"
                      className={`photo-pick${picked ? ' photo-pick-on' : ''}`}
                      aria-pressed={picked}
                      onClick={() => togglePhoto(p.id)}>
                      <img src={p.thumb} alt={p.label} />
                      <span>{p.label}</span>
                    </button>
                    {picked && (
                      <>
                        <button type="button"
                          className="w3-button w3-round poster-btn"
                          style={{ width: '100%', marginTop: 4 }}
                          aria-pressed={isHero}
                          onClick={() => set('heroPhotoId', isHero ? null : p.id)}>
                          {isHero ? '★ Top photo' : 'Make top photo'}
                        </button>
                        {/* Every frame on the page has a fixed shape, so a
                            picture is cropped to fill it. Right for a photo
                            of the green, wrong for a logo — which is what
                            these two are for. The setting belongs to the
                            picture, so it holds wherever the picture is
                            used. */}
                        <select className="w3-select" style={{ marginTop: 4 }}
                          aria-label={`How "${p.label}" is shown`}
                          value={fitOf(p.id).fit}
                          onChange={e => setFit(p.id, { fit: e.target.value })}>
                          <option value="cover">Fill the space</option>
                          <option value="contain">Show all of it</option>
                        </select>
                        {fitOf(p.id).fit === 'cover' && (
                          <select className="w3-select" style={{ marginTop: 4 }}
                            aria-label={`Which part of "${p.label}" to keep`}
                            value={fitOf(p.id).focus}
                            onChange={e => setFit(p.id, { focus: e.target.value })}>
                            <option value="centre">Keep the middle</option>
                            <option value="top">Keep the top</option>
                            <option value="bottom">Keep the bottom</option>
                            <option value="left">Keep the left</option>
                            <option value="right">Keep the right</option>
                          </select>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Section>

      <Section label={SECTION_LABEL.contact} action={
        <Toggle ariaLabel="Show contact details" checked={!!site.sections.contact}
          onChange={() => setSection('contact', !site.sections.contact)} />
      }>
        <div className="gs-field-grid">
          <Field label="Street address" htmlFor="site-addr1">
            <input id="site-addr1" className="w3-input" maxLength={120}
              value={site.contact.addressLine1} onChange={e => setContact('addressLine1', e.target.value)} />
          </Field>
          <Field label="More address" htmlFor="site-addr2" help="Optional.">
            <input id="site-addr2" className="w3-input" maxLength={120}
              value={site.contact.addressLine2} onChange={e => setContact('addressLine2', e.target.value)} />
          </Field>
        </div>
        <div className="gs-field-grid">
          <Field label="Suburb" htmlFor="site-suburb">
            <input id="site-suburb" className="w3-input" maxLength={80}
              value={site.contact.suburb} onChange={e => setContact('suburb', e.target.value)} />
          </Field>
          <Field label="Town or city" htmlFor="site-city">
            <input id="site-city" className="w3-input" maxLength={80}
              value={site.contact.city} onChange={e => setContact('city', e.target.value)} />
          </Field>
          <Field label="Postcode" htmlFor="site-postcode">
            <input id="site-postcode" className="w3-input" maxLength={16}
              value={site.contact.postcode} onChange={e => setContact('postcode', e.target.value)} />
          </Field>
        </div>
        <div className="gs-field-grid">
          <Field label="Phone" htmlFor="site-phone">
            <input id="site-phone" className="w3-input" maxLength={40}
              value={site.contact.phone} onChange={e => setContact('phone', e.target.value)} />
          </Field>
          <Field label="Email" htmlFor="site-email" help="The club’s own address, not a person’s.">
            <input id="site-email" className="w3-input" type="email" maxLength={160}
              value={site.contact.email} onChange={e => setContact('email', e.target.value)} />
          </Field>
        </div>
        <Field label="When you are open" htmlFor="site-hours"
          help="Say it however your club says it — “Club day Saturday, roll-up from 12:30”.">
          <textarea id="site-hours" className="w3-input" maxLength={400}
            style={{ minHeight: 70, resize: 'vertical' }}
            value={site.contact.hours} onChange={e => setContact('hours', e.target.value)} />
        </Field>
      </Section>

      <Section label={SECTION_LABEL.links} action={
        <Toggle ariaLabel="Show links" checked={!!site.sections.links}
          onChange={() => setSection('links', !site.sections.links)} />
      }>
        {/* Kind, not free text: the label and icon come from the kind, so no
            club ends up with a row of links all saying "Click here". */}
        {site.links.map((l, i) => (
            <div key={i} className="gs-field-grid"
              style={{ '--gs-cols': l.kind === 'other' ? '1fr 2fr 1fr auto' : '1fr 2fr auto', marginBottom: 8 }}>
              <Field label={i === 0 ? 'What it is' : ''}>
                <select className="w3-select" value={l.kind} aria-label={`Link ${i + 1} kind`}
                  onChange={e => setLink(i, { kind: e.target.value })}>
                  {data.linkKinds.map(k => (
                    <option key={k.kind} value={k.kind}>{k.label || 'Other website'}</option>
                  ))}
                </select>
              </Field>
              <Field label={i === 0 ? 'Address' : ''}>
                <input className="w3-input" type="url" maxLength={300} value={l.url}
                  aria-label={`Link ${i + 1} address`}
                  onChange={e => setLink(i, { url: e.target.value })} />
              </Field>
              {l.kind === 'other' && (
                <Field label={i === 0 ? 'Call it' : ''}>
                  <input className="w3-input" maxLength={40} value={l.label}
                    aria-label={`Link ${i + 1} label`}
                    onChange={e => setLink(i, { label: e.target.value })} />
                </Field>
              )}
              <Field label={i === 0 ? ' ' : ''}>
                <button type="button" className="ml-topic-bin"
                  aria-label={`Remove link ${i + 1}`}
                  onClick={() => set('links', site.links.filter((_, j) => j !== i))}>
                  <i className="fa fa-trash-o" aria-hidden="true"></i>
                </button>
              </Field>
            </div>
        ))}
        <button type="button" className="w3-button w3-round poster-btn"
          disabled={site.links.length >= (data.maxLinks || 6)} onClick={addLink}>
          <i className="fa fa-plus w3-margin-right"></i>Add a link
        </button>
        {site.links.length >= (data.maxLinks || 6) && (
          <p className="poster-note">That is as many as a page can carry.</p>
        )}
      </Section>

      {/* Joining, renewing, logging in and signing up for news. The club
          chooses which appear, what they are called, and where they sit —
          the page itself has no words of its own for any of them. */}
      <Section label="Buttons and sign-ups" action={
        <span style={{ fontSize: 'var(--gs-text-sm)', color: 'var(--gs-text-muted)' }}>
          {/* Not "2 of 4": four is every button that exists, not a ration,
              and reading it as a limit would suggest a club is running out
              of something it is not. */}
          {site.actions.length === 0
            ? 'none turned on'
            : `${site.actions.length} turned on`}
        </span>
      }>
        {/* Kept: "blank uses the usual wording" is not guessable from an
            empty box, and the placeholder only shows what that wording is. */}
        <p style={{ marginTop: 0, color: 'var(--gs-text-muted)', fontSize: 'var(--gs-text-sm)' }}>
          Leave a name blank to use the usual wording.
        </p>
        {(data.actionKinds || []).map(k => {
          const chosen = site.actions.find(a => a.kind === k.kind);
          return (
            <div key={k.kind} className="gs-field-grid"
              style={{ '--gs-cols': 'auto 2fr 2fr', alignItems: 'end', marginBottom: 'var(--gs-space-3)' }}>
              <Field label=" ">
                <label className="poster-switch">
                  <Toggle
                    ariaLabel={`Show ${k.label}`}
                    checked={!!chosen}
                    onChange={() => (chosen
                      ? set('actions', site.actions.filter(a => a.kind !== k.kind))
                      : set('actions', [...site.actions,
                        { kind: k.kind, label: '', place: k.places[0] }]))} />
                  <span>{k.label}</span>
                </label>
              </Field>
              {/* The one kind of placeholder kept anywhere on this screen.
                  It is not an invented example — it is the exact wording the
                  button will carry if this is left empty, so the grey text is
                  a true preview rather than a suggestion to be mistaken for
                  content. Same for the headings below. */}
              <Field label="Call it" htmlFor={`act-${k.kind}`}>
                <input id={`act-${k.kind}`} className="w3-input" maxLength={40}
                  placeholder={k.label} disabled={!chosen}
                  value={chosen ? chosen.label : ''}
                  onChange={e => setAction(k.kind, { label: e.target.value })} />
              </Field>
              <Field label="Where" htmlFor={`place-${k.kind}`}>
                <select id={`place-${k.kind}`} className="w3-select"
                  disabled={!chosen} value={chosen ? chosen.place : k.places[0]}
                  onChange={e => setAction(k.kind, { place: e.target.value })}>
                  {k.places.map(p => <option key={p} value={p}>{PLACE_LABEL[p] || p}</option>)}
                </select>
              </Field>
            </div>
          );
        })}
      </Section>

      {/* The words above each part of the page. Editable for the same reason
          as everything else: a club that says "The green" rather than "Where"
          should not have to live with ours. Blank hides the heading. */}
      <Section label="Wording">
        {/* Kept: an empty box normally means "use the default", and here it
            means the opposite — no heading at all. */}
        <p style={{ marginTop: 0, color: 'var(--gs-text-muted)', fontSize: 'var(--gs-text-sm)' }}>
          Empty shows no heading at all.
        </p>
        <div className="gs-field-grid">
          {/* No "above the welcome" any more: that block is a piece, and a
              piece carries its own heading. */}
          <Field label="Above the contact details" htmlFor="head-contact">
            <input id="head-contact" className="w3-input" maxLength={60}
              placeholder={(data.defaultHeadings || {}).contact}
              value={site.headings.contact} onChange={e => setHeading('contact', e.target.value)} />
          </Field>
        </div>
        <div className="gs-field-grid">
          {['where', 'when', 'contactHow', 'maps'].map(k => (
            <Field key={k} label={{ where: 'The address', when: 'The hours', contactHow: 'Phone and email', maps: 'The maps link' }[k]}
              htmlFor={`head-${k}`}>
              <input id={`head-${k}`} className="w3-input" maxLength={60}
                placeholder={(data.defaultHeadings || {})[k]}
                value={site.headings[k]} onChange={e => setHeading(k, e.target.value)} />
            </Field>
          ))}
        </div>
      </Section>

      {error && <div className="poster-error" role="alert">{error}</div>}

      <div style={{ display: 'flex', gap: 'var(--gs-space-3)', alignItems: 'center', marginTop: 'var(--gs-space-4)', flexWrap: 'wrap' }}>
        <button type="button" className="w3-button w3-round poster-btn-primary"
          disabled={!dirty || saving} onClick={save}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="w3-button w3-round poster-btn"
          onClick={() => setPreviewing(v => !v)}>
          {previewing ? 'Hide preview' : 'Preview'}
        </button>
        <span style={{ color: 'var(--gs-text-muted)', fontSize: 'var(--gs-text-sm)' }}>
          {dirty ? 'Not saved yet.' : 'Everything saved.'}
        </span>
      </div>

      {previewing && (
        <Section label="Preview">
          {/* The preview reads what is saved, not what is typed, because the
              page is drawn from the API rather than from this form. Saying so
              is better than quietly showing somebody yesterday's wording
              while today's sits unsaved beside it. */}
          {dirty && (
            <p className="poster-note" style={{ marginTop: 0 }}>
              Showing what is saved. Save to see the changes you have just made.
            </p>
          )}
          <ClubSite key={previewKey} slug={slug} embedded />
        </Section>
      )}
    </>
  );
}

export default ClubWebsitePanel;
